// Zod schemas mirroring the backend Pydantic response models in
// damperlib/api_models.py. Field names + nullability must stay in
// lockstep — the pytest contract test snapshots app.openapi() and
// frontend/tests/api-contract.test.ts asserts every required field
// in that snapshot exists here.
import { z } from "zod";

export const ProvenanceInputStatusSchema = z.object({
  status: z.enum(["PASS", "FALLBACK", "ABSENT"]),
  reason: z.string().nullable(),
  source: z.string().nullable().optional(),
});

export const ProvenanceSummarySchema = z.object({
  trusted: z.boolean(),
  inputs: z.record(z.string(), ProvenanceInputStatusSchema),
});

export const RunArtifactRefSchema = z.object({
  id: z.number().int(),
  kind: z.string(),
  payload_path: z.string(),
  payload_sha256: z.string(),
  payload_bytes: z.number().int(),
});

export const PredictionStatusSchema = z.enum([
  "pending", "running", "complete", "failed", "cancelled",
]);

export const RunSchema = z.object({
  id: z.number().int(),
  test_id: z.number().int(),
  run_number: z.number().int(),
  baseline_run_id: z.number().int().nullable(),
  damper_setting: z.record(z.string(), z.unknown()),
  source: z.string(),
  status: PredictionStatusSchema,
  status_detail: z.string().nullable(),
  kpi_scores: z.record(z.string(), z.unknown()).nullable(),
  prediction_id: z.number().int().nullable(),
  artifacts: z.array(RunArtifactRefSchema),
  provenance: ProvenanceSummarySchema.nullable(),
  created_at: z.string(),
  // Progress surface (2026-06-12): job_queue timing joined by the
  // backend. Optional so pre-build payloads (and fixtures) stay valid.
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  queue_ahead: z.number().int().nullable().optional(),
});

export const TestSchema = z.object({
  id: z.number().int(),
  name: z.string().nullable(),
  track_code: z.string(),
  replay_recording_id: z.number().int().nullable(),
  baseline_setup_name: z.string().nullable(),
  source_event: z.string().nullable(),
  kpi_targets: z.record(z.string(), z.unknown()),
  status: z.string(),
  runs: z.array(RunSchema),
  provenance: ProvenanceSummarySchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const TestListItemSchema = TestSchema.omit({ runs: true }).extend({
  // Activity aggregates (2026-06-12): the Studies list shows in-flight
  // work without each Test's runs. Defaulted so older payloads parse.
  active_runs: z.number().int().default(0),
  ar_active: z.boolean().default(false),
});

export const CachedEventSchema = z.object({
  event_id: z.string(),
  track_code: z.string(),
  season_code: z.string(),
  event_class: z.enum(["track", "rig_characterisation", "unknown_prefix"]),
  recording_count: z.number().int(),
  recording_default_id: z.string().nullable(),
  setup_xlsm_present: z.boolean(),
  pvp_present: z.boolean(),
  // Deprecated compatibility alias: mirrors study_ready on current backends.
  trusted: z.boolean(),
  gate_status: z.enum(["PASS", "FAIL", "QUARANTINED", "NOT_OK", "UNKNOWN"]),
  gate_detail: z.string(),
  gate_csv: z.string().nullable(),
  study_ready: z.boolean(),
  recommended_baseline_id: z.number().int().nullable(),
  rejection_reasons: z.array(z.string()),
  selectable: z.boolean(),
  // Layer 2: structured ingest status; missing_inputs is the normalised list
  // of artifact kinds the local cache is missing. 2026-06-03: 'unavailable'
  // split into unavailable_nas_down (share dropped) / unavailable_source_missing
  // (genuinely absent) / needs_authority_check (setup XLSM not in a standard
  // subdir) so a dropped mount never masquerades as missing data. 'unavailable'
  // kept as a legacy value.
  ingest_status: z.enum([
    "cached", "needs_ingest", "unavailable",
    "unavailable_nas_down", "unavailable_source_missing", "needs_authority_check",
  ]),
  missing_inputs: z.array(z.string()).default([]),
  // True when the event is runnable but its OPTIONAL InputInfo.mat is absent
  // (the rig falls back to the setup XLSM's motion ratios) — a warning, not a
  // blocker. Optional in the wire schema (always present on real responses;
  // absent => treated as false).
  input_info_missing: z.boolean().optional(),
});

export type Test = z.infer<typeof TestSchema>;
export type TestListItem = z.infer<typeof TestListItemSchema>;
export type Run = z.infer<typeof RunSchema>;
export type RunArtifactRef = z.infer<typeof RunArtifactRefSchema>;
export type ProvenanceSummary = z.infer<typeof ProvenanceSummarySchema>;
export type ProvenanceInputStatus = z.infer<typeof ProvenanceInputStatusSchema>;
export type CachedEvent = z.infer<typeof CachedEventSchema>;


// ---------- Track inputs (Phase 8.5b selector view-model) ----------
// Mirrors damperlib/api_models.py TrackInput + TrackInputSource. A presentation
// grouping over source_event/track_code; `key` is the selector identity, and
// track_code is nullable for new venues with no runtime code yet.
export const TrackInputConfidenceSchema = z.enum([
  "gate_clean",
  "selected_under_investigation",
  "trusted_unverified",
  "degraded",
]);
export const TrackInputTierSchema = z.enum([
  "real_7post",
  "track_replay_inverse",
  "synthetic_candidate",
  "unavailable",
  "pre_event_no_reference",
]);
export const TrackInputSourceSchema = z.object({
  source_event: z.string(),
  confidence_state: TrackInputConfidenceSchema,
  warnings: z.array(z.string()),
});
// Fable P4: a staged own-event setup XLSM offered as the car platform for a
// replay-only venue. setup_id goes to kpi_targets.setup_file at create.
export const StagedSetupRefSchema = z.object({
  setup_id: z.string(),
  season: z.string(),
  track_code: z.string().nullable().optional(),
  car: z.string().nullable().optional(),
  event_label: z.string().optional().default(""),
  label: z.string().optional().default(""),
  is_race_event: z.boolean().optional().default(false),
});
export const TrackInputSchema = z.object({
  key: z.string(),
  track_code: z.string().nullable(),
  layout: z.string(),
  display_name: z.string(),
  tier: TrackInputTierSchema,
  demanded: z.boolean(),
  runnable: z.boolean(),
  recommended_default: TrackInputSourceSchema.nullable(),
  alternates: z.array(TrackInputSourceSchema),
  // Fable P2: deployment holds an ingested track-replay lap for this track.
  // Replay-only tracks (no 7-post source) arrive runnable with
  // tier=track_replay_inverse and need a car-platform source at create:
  // the venue's own staged setup when available, else a setup-donor campaign.
  replay_available: z.boolean(),
  // Fable P4: staged own-event setups (default-pick order). Non-empty ⇒ the
  // wizard defaults to the venue's own setup; no donor required.
  own_setups: z.array(StagedSetupRefSchema).optional().default([]),
  warnings: z.array(z.string()),
});
export type TrackInputConfidence = z.infer<typeof TrackInputConfidenceSchema>;
export type TrackInputTier = z.infer<typeof TrackInputTierSchema>;
export type TrackInputSource = z.infer<typeof TrackInputSourceSchema>;
export type StagedSetupRef = z.infer<typeof StagedSetupRefSchema>;
export type TrackInput = z.infer<typeof TrackInputSchema>;

// Fable P3 recipe rail: what a Study created right now would use for each
// of the four inputs — resolved server-side BEFORE create.
export const RecipePartSchema = z.object({
  kind: z.string(),
  summary: z.string(),
  source_event: z.string().nullable().optional(),
  donor: z.boolean().nullable().optional(),
  default_type: z.string().nullable().optional(),
  // Fable P4: present on platform parts of kind=own_event_setup.
  setup_file: z.string().nullable().optional(),
});
export const StudyRecipeSchema = z.object({
  key: z.string(),
  track_code: z.string().nullable(),
  layout: z.string(),
  actuation: RecipePartSchema,
  platform: RecipePartSchema,
  tyre: RecipePartSchema,
  damper: RecipePartSchema,
});
export type StudyRecipe = z.infer<typeof StudyRecipeSchema>;


// ---------- Auto Research (Phase 5A) ----------
// Mirrors damperlib/api_models.py AutoResearchSession +
// AutoResearchSessionListItem. State enum tracks the SQL CHECK
// constraint in migration 025.

export const AutoResearchStateSchema = z.enum([
  "running", "stopped", "completed", "failed",
]);

export const AutoResearchMemberRunRefSchema = z.object({
  run_id: z.number().int(),
  proposal_index: z.number().int(),
  status: PredictionStatusSchema,
});

export const AutoResearchSessionSchema = z.object({
  id: z.number().int(),
  test_id: z.number().int(),
  state: AutoResearchStateSchema,
  max_runs: z.number().int(),
  seed: z.number().int(),
  target_kpi: z.string().nullable(),
  target_direction: z.enum(["max", "min"]).nullable(),
  target_value: z.number().nullable(),
  // Phase 5B-0 follow-up: objective contract echoed by the server.
  objective_id: z.string().nullable(),
  // T3 Pareto: the full objective set when optimising a front (≥2);
  // objective_id is objective_ids[0]. null for single-objective sessions.
  // Optional: the backend field has a default (not required), and older
  // payloads / fixtures may omit it.
  objective_ids: z.array(z.string()).nullable().optional(),
  objective_target_value: z.number().nullable(),
  constraint_overrides: z.record(z.string(), z.number()).nullable(),
  // Phase 5C foundation: surface which generator drove the session
  // plus Claude-loop bookkeeping. Deterministic sessions read
  // generator='click_sweep' + sensible defaults.
  generator: z.enum(["click_sweep", "claude"]),
  model: z.string().nullable(),
  static_prompt_hash: z.string().nullable(),
  current_proposal_index: z.number().int(),
  total_input_tokens: z.number().int(),
  total_output_tokens: z.number().int(),
  total_cache_read_tokens: z.number().int(),
  total_cache_creation_tokens: z.number().int(),
  estimated_cost_usd: z.number().nullable(),
  target_met: z.boolean(),
  best_run_id: z.number().int().nullable(),
  best_score: z.number().nullable(),
  member_runs: z.array(AutoResearchMemberRunRefSchema),
  runs_terminal: z.number().int(),
  runs_total: z.number().int(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
  error: z.string().nullable(),
});

export const AutoResearchSessionListItemSchema = z.object({
  id: z.number().int(),
  test_id: z.number().int(),
  state: AutoResearchStateSchema,
  max_runs: z.number().int(),
  runs_total: z.number().int(),
  runs_terminal: z.number().int(),
  best_run_id: z.number().int().nullable(),
  best_score: z.number().nullable(),
  target_met: z.boolean(),
  // Phase 5B-0 follow-up: list items carry the objective contract
  // too so the UI can show 'which sessions used which objective'
  // without a fan-out of per-session GETs.
  objective_id: z.string().nullable(),
  objective_target_value: z.number().nullable(),
  constraint_overrides: z.record(z.string(), z.number()).nullable(),
  // Phase 5C foundation — same generator field as the full session
  // schema so the list view can badge Claude vs deterministic.
  generator: z.enum(["click_sweep", "claude"]),
  created_at: z.string(),
  finished_at: z.string().nullable(),
});

export type AutoResearchState = z.infer<typeof AutoResearchStateSchema>;
export type AutoResearchMemberRunRef =
  z.infer<typeof AutoResearchMemberRunRefSchema>;
export type AutoResearchSession =
  z.infer<typeof AutoResearchSessionSchema>;
export type AutoResearchSessionListItem =
  z.infer<typeof AutoResearchSessionListItemSchema>;
